import getAccessToken from 'services/getAccessToken';

import { msalInstance } from 'configs/msalInstance';

export class ResponseError extends Error {
  public response: Response;

  constructor(response: Response) {
    super(response.statusText);
    this.response = response;
  }
}
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
async function parseJSON(response: Response) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  const responseData = await response.json();
  return responseData;
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
function checkStatus(response: Response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  const error = new ResponseError(response);
  error.response = response;
  throw error;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export async function request(url: string, options: RequestInit = {}) {
  const accessToken = await getAccessToken(msalInstance);
  const headers = new Headers(options.headers);
  const bearer = `Bearer ${accessToken}`;

  headers.append('Authorization', bearer);

  // Only set Content-Type to application/json if:
  // 1. No Content-Type is already set in options.headers
  // 2. Body is not FormData (which needs multipart/form-data set automatically by browser)
  const hasContentType = headers.has('Content-Type');
  const isFormData = options.body instanceof FormData;

  if (!hasContentType && !isFormData) {
    headers.append('Content-Type', 'application/json');
  }

  options.headers = headers;
  const fetchResponse = await fetch(url, options);
  const response = checkStatus(fetchResponse);
  return await parseJSON(response);
}

interface RequestOptions extends RequestInit {
  responseType?: 'json' | 'blob' | 'text' | 'arrayBuffer' | 'formData';
}

/**
 * Enhanced request function that supports different response types
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch" plus responseType
 *
 * @return {Promise<any>}     The response data in the requested format with metadata
 */
export async function requestWithResponseType(url: string, options: RequestOptions = {}) {
  const { responseType = 'json', ...fetchOptions } = options;
  const accessToken = await getAccessToken(msalInstance);
  const headers = new Headers(fetchOptions.headers);
  const bearer = `Bearer ${accessToken}`;

  headers.append('Authorization', bearer);

  const contentType: string = headers.get('Content-Type') || '';
  // Only set Content-Type to application/json if:
  // 1. No Content-Type is already set in options.headers
  // 2. Body is not FormData (which needs multipart/form-data set automatically by browser)
  // 3. We're expecting a JSON response (for consistency)
  const hasContentType = Boolean(contentType);
  const isFormData = fetchOptions.body instanceof FormData;

  if (!hasContentType && !isFormData && responseType === 'json') {
    headers.append('Content-Type', 'application/json');
  }

  fetchOptions.headers = headers;
  const fetchResponse = await fetch(url, fetchOptions);
  const response = checkStatus(fetchResponse);
  const responseContentType = response.headers.get('Content-Type') || '';

  // Handle different response types
  let data: any;
  switch (responseType) {
    case 'blob':
      data = await response.blob();
      break;
    case 'text':
      data = await response.text();
      break;
    case 'arrayBuffer':
      data = await response.arrayBuffer();
      break;
    case 'formData':
      data = await response.formData();
      break;
    case 'json':
    default:
      data = await parseJSON(response);
      break;
  }

  // Return data with Content-Disposition header
  return {
    data,
    responseContentType,
  };
}
